import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Avatar,
  Divider,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { useState } from "react";

export const SearchSavedQuerySidebar = ({
  myChannels = [],
  handleNavigate = () => {},
  selectedSectionIndex = 0,
  sidebarHeight = "100%", // inherit from parent unless overridden
}) => {
  const ownedChannels = myChannels.filter((s) => s.is_owner);
  const sharedChannels = myChannels.filter((s) => !s.is_owner);
  
  // State for collapsible sections
  const [ownedExpanded, setOwnedExpanded] = useState(true);
  const [sharedExpanded, setSharedExpanded] = useState(true);

  return (
    <Box
      sx={{
        width: "100%",
        height: sidebarHeight,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          px: 1.5, 
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 12,
              bgcolor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ChatBubbleOutlineIcon sx={{ color: "#64748b", fontSize: 14 }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
            Saved Queries
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 10, color: "#6B7280", mt: 0.25 }}>
          {myChannels.length} channels
        </Typography>
      </Box>

      {/* Sections container */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          "::-webkit-scrollbar": { width: 6 },
          "::-webkit-scrollbar-thumb": {
            background: "#D1D5DB", // Application gray
            borderRadius: 3,
          },
          "::-webkit-scrollbar-track": {
            background: "transparent",
          },
        }}
      >
        {ownedChannels.length > 0 && (
          <Section
            label="OWNED BY ME"
            items={ownedChannels}
            all={myChannels}
            selectedSectionIndex={selectedSectionIndex}
            onPick={handleNavigate}
            expanded={ownedExpanded}
            onToggle={() => setOwnedExpanded(!ownedExpanded)}
          />
        )}

        {sharedChannels.length > 0 && (
          <Section
            label="SHARED WITH ME"
            items={sharedChannels}
            all={myChannels}
            selectedSectionIndex={selectedSectionIndex}
            onPick={handleNavigate}
            expanded={sharedExpanded}
            onToggle={() => setSharedExpanded(!sharedExpanded)}
          />
        )}
      </Box>
    </Box>
  );
};

function Section({ label, items, all, selectedSectionIndex, onPick, expanded, onToggle }) {
  const isOwned = label === "OWNED BY ME";
  
  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
      <SectionHeaderRight 
        label={label} 
        expanded={expanded} 
        onToggle={onToggle}
        isOwned={isOwned}
        count={items.length}
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List sx={{ p: 0, pb: 0.5 }}>
          {items.map((section, idx) => {
            const originalIdx = all.findIndex(
              (ch) => ch.channel_id === section.channel_id
            );
            const isSelected = selectedSectionIndex === originalIdx;

            return (
              <ChannelRowClean
                key={`${label}-${section.channel_id ?? idx}`}
                section={section}
                isSelected={isSelected}
                onClick={() => onPick(section, originalIdx)}
                isOwned={isOwned}
              />
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
}

function SectionHeaderRight({ label, expanded, onToggle, isOwned, count }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        minHeight: 32,
        cursor: "pointer",
        bgcolor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        "&:hover": { bgcolor: "#f1f5f9" },
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
        {isOwned ? (
          <PersonIcon sx={{ fontSize: 14, color: "#64748b" }} />
        ) : (
          <GroupIcon sx={{ fontSize: 14, color: "#64748b" }} />
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#111827" }}>
          {label}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 600,
            bgcolor: "#e2e8f0",
            color: "#475569",
            borderRadius: 8,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>
      <IconButton size="small" disableRipple sx={{ color: "#6B7280", p: 0.25, "&:hover": { bgcolor: "transparent" }, transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
        {expanded ? (
          <ExpandLessRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <ExpandMoreRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Box>
  );
}

function ChannelRowClean({ section, isSelected, onClick, isOwned }) {
  const channel_name = section.channel_name;
  const memberCount = section.channel_members?.length || 0;

  return (
    <ListItemButton
      onClick={onClick}
      selected={isSelected}
      sx={{
        mx: 0.75,
        my: 0.25,
        borderRadius: 12,
        alignItems: "center",
        py: 0.75,
        px: 1.25,
        minHeight: 40,
        gap: 1,
        bgcolor: isSelected ? "#f1f5f9" : "transparent",
        "&.Mui-selected": { bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } },
        "&:hover": { 
          bgcolor: isSelected ? "#e2e8f0" : "#f8fafc",
          borderColor: isSelected ? "#e2e8f0" : "#e2e8f0",
        },
        border: isSelected ? "1px solid #cbd5e1" : "1px solid transparent",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 12,
          bgcolor: isSelected ? "#cbd5e1" : "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: isSelected ? "#334155" : "#64748b" }} />
      </Box>
      
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: isSelected ? "#0f172a" : "#334155",
            fontWeight: isSelected ? 600 : 500,
            fontSize: 12,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {channel_name}
        </Typography>
        
        {memberCount > 0 && (
          <Typography sx={{ color: isSelected ? "#475569" : "#64748b", fontSize: 10 }}>
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
}

export default SearchSavedQuerySidebar;
